import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useTemplateStore } from '@/stores/template'
import { createLogger } from '@/shared/logger'

const logger = createLogger('ActivityStore')
// Configuration based on environment variable for activity logs control
const enableActivityLogs = import.meta.env.VITE_ENABLE_ACTIVITY_LOGS === 'true'

// Time constants in milliseconds
const MINUTE = 60 * 1000

// Development environment settings
// const ONE_MINUTE = 1 * MINUTE // 60,000 ms (1 minute)
// const CHECK_INTERVAL = 15 * 1000 // Check every 15 seconds

// Production environment settings
const SIXTY_MINUTES = 60 * MINUTE // 3,600,000 ms (60 minutes)
const CHECK_INTERVAL = 5 * MINUTE // 300,000 ms (5 minutes)

// Inactivity timeout used by the system
const TIMEOUT = SIXTY_MINUTES // Use ONE_MINUTE for testing or SIXTY_MINUTES for production

export const useActivityStore = defineStore('activity', {
  id: 'activity',
  state: () => ({
    // Volatile state (reset on each page load)
    lastActivity: Date.now(),
    isAlertOpen: false,
    shouldReload: false,
    inactivityTimeout: TIMEOUT,
    lastActivityEvent: null,
    checkIntervalId: null,
    sessionId: null,

    // Persistent state (maintained between page loads)
    wasLoggedOutDueToInactivity: false, // Indicator for notification display after login
    logoutTimestamp: null // Logout timestamp for recency verification
  }),

  getters: {
    isInactive: state =>
      computed(() => {
        return Date.now() - state.lastActivity >= TIMEOUT
      }),

    timeRemaining: state =>
      computed(() => {
        return Math.max(0, TIMEOUT - (Date.now() - state.lastActivity))
      })
  },

  actions: {
    // Method to reset the activity state
    resetActivity() {
      const currentTime = Date.now()
      this.lastActivity = currentTime
      this.isAlertOpen = false
      this.shouldReload = false
      this.lastActivityEvent = 'session_start'
      this.sessionId = `session_${currentTime}`

      // Records activity reset when logs are enabled
      if (enableActivityLogs) {
        logger.log('🔄 Activity reset:', {
          time: new Date(currentTime).toLocaleTimeString(),
          sessionId: this.sessionId
        })
      }
    },

    updateLastActivity(event, details = '') {
      const currentTime = Date.now()
      this.lastActivity = currentTime
      this.lastActivityEvent = event
      this.isAlertOpen = false
      this.shouldReload = false

      // Sends message to other tabs
      if (window.BroadcastChannel) {
        const activityChannel = new BroadcastChannel('publinker_activity')
        activityChannel.postMessage({
          type: 'activity_update',
          timestamp: currentTime,
          event,
          details,
          sessionId: this.sessionId
        })
        activityChannel.close()
      }

      // Filtered activity logging to reduce noise
      // Movement and scroll events are omitted due to their frequency
      if (enableActivityLogs && event !== 'mousemove' && event !== 'scroll') {
        logger.log('🔄 Activity:', event, details ? `(${details})` : '')
      }
    },

    // Method to synchronize activity between tabs
    syncActivity(timestamp, event = 'unknown', details = '', sessionId = null) {
      // Update conditioned on the validity of timestamp and session
      if (
        timestamp > this.lastActivity &&
        (!this.sessionId || sessionId === this.sessionId)
      ) {
        this.lastActivity = timestamp
        this.lastActivityEvent = event
        this.isAlertOpen = false
        this.shouldReload = false

        // Records tab synchronization
        if (enableActivityLogs) {
          logger.log('🔄 Sync activity:', {
            from: 'other tab',
            event,
            sessionId,
            details: details || 'No details provided'
          })
        }
      }
    },

    // Method to check for inactivity
    checkInactivity() {
      if (this.isAlertOpen || this.shouldReload) {
        return false
      }

      const templateStore = useTemplateStore()
      const timeSinceLastActivity = Date.now() - this.lastActivity
      const minutesSinceLastActivity = Math.round(
        timeSinceLastActivity / MINUTE
      )

      // Counter reset when a page load is detected
      if (templateStore.settings.pageLoader) {
        if (enableActivityLogs) {
          logger.log('⏳ Loader detected - resetting timer')
        }
        this.updateLastActivity('page_loader', 'Page loading detected')
        return false
      }

      // Intelligent log control to reduce noise
      const shouldLog =
        enableActivityLogs &&
        (timeSinceLastActivity >= TIMEOUT ||
          timeSinceLastActivity % MINUTE < CHECK_INTERVAL)

      if (shouldLog) {
        const timeoutReached = timeSinceLastActivity >= TIMEOUT

        if (timeoutReached) {
          // Highlighted log when timeout is reached
          logger.warn('⏱️ INACTIVITY TIMEOUT REACHED', {
            inactive: `${minutesSinceLastActivity}min (${timeSinceLastActivity}ms)`,
            timeout: `${TIMEOUT / MINUTE}min`,
            lastEvent: this.lastActivityEvent
          })
        } else {
          // Periodic logging of inactivity state
          logger.log('⏱️ Inactivity check:', {
            inactive: `${minutesSinceLastActivity}min`,
            threshold: `${TIMEOUT / MINUTE}min`,
            remaining: `${Math.ceil((TIMEOUT - timeSinceLastActivity) / MINUTE)}min`
          })
        }
      }

      // Effective verification of inactivity timeout
      if (timeSinceLastActivity >= TIMEOUT) {
        // Records session expiration
        if (enableActivityLogs) {
          logger.warn('🚨 Session expired due to inactivity', {
            inactiveTime: `${minutesSinceLastActivity}min`,
            lastEvent: this.lastActivityEvent || 'none'
          })
        }

        this.forceReloadDueToInactivity()
        return true
      }

      return false
    },

    forceReloadDueToInactivity() {
      if (this.isAlertOpen || this.shouldReload) return

      const inactiveMinutes = Math.round(
        (Date.now() - this.lastActivity) / MINUTE
      )

      // Records the forced reload operation
      if (enableActivityLogs) {
        logger.warn('⚠️ Forcing page reload due to inactivity', {
          sessionStarted: new Date(this.lastActivity).toLocaleTimeString(),
          currentTime: new Date().toLocaleTimeString(),
          inactiveTime: `${inactiveMinutes}min`,
          lastEvent: this.lastActivityEvent || 'none'
        })
      }

      // State configuration before reload
      this.wasLoggedOutDueToInactivity = true
      this.logoutTimestamp = Date.now()
      this.shouldReload = true

      // Notification to other tabs about session expiration
      if (window.BroadcastChannel) {
        const activityChannel = new BroadcastChannel('publinker_activity')
        activityChannel.postMessage({
          type: 'session_expired',
          timestamp: Date.now(),
          sessionId: this.sessionId
        })
        activityChannel.close()
      }

      // Page reload execution
      window.location.reload()
    },

    // Legacy method maintained for compatibility, redirects to the new behavior
    async showInactivityAlert() {
      this.forceReloadDueToInactivity()
    },

    checkIfLoggedOutDueToInactivity() {
      const templateStore = useTemplateStore()

      // Verification of inactivity flag and timestamp recency
      const isRecent =
        this.logoutTimestamp &&
        templateStore.isTimestampRecent(this.logoutTimestamp)

      if (this.wasLoggedOutDueToInactivity && isRecent) {
        // State cleanup after confirmation
        this.wasLoggedOutDueToInactivity = false
        this.logoutTimestamp = null

        // Records confirmation of logout due to inactivity
        if (enableActivityLogs) {
          logger.log(
            '✅ Confirmed recent logout due to inactivity - will show notification'
          )
        }
        return true
      }

      // Cleanup of old flags without showing notification
      if (this.wasLoggedOutDueToInactivity) {
        this.wasLoggedOutDueToInactivity = false
        this.logoutTimestamp = null

        if (enableActivityLogs) {
          logger.log(
            'ℹ️ Found stale inactivity flag - clearing without showing toast'
          )
        }
      }

      return false
    },

    // Method to start activity monitoring
    startActivityCheck() {
      // State initialization
      this.resetActivity()

      // Updating timeout reference in state
      this.inactivityTimeout = TIMEOUT

      // Clearing previous intervals
      if (this.checkIntervalId) {
        clearInterval(this.checkIntervalId)
      }

      // Configuration of periodic verification interval
      this.checkIntervalId = setInterval(() => {
        this.checkInactivity()
      }, CHECK_INTERVAL)

      // Records the start of monitoring
      if (enableActivityLogs) {
        logger.log('✅ Activity monitor started', {
          checkInterval: `${CHECK_INTERVAL / 1000}s`,
          timeout: `${TIMEOUT / MINUTE}min`,
          sessionId: this.sessionId
        })
      }
    },

    stopActivityCheck() {
      if (this.checkIntervalId) {
        clearInterval(this.checkIntervalId)
        this.checkIntervalId = null

        if (enableActivityLogs) {
          logger.log('🛑 Activity monitor stopped')
        }
      }
    }
  },

  persist: {
    // Selective persistence of only essential data for functionality
    // between page loads
    paths: ['wasLoggedOutDueToInactivity', 'logoutTimestamp']
  }
})
